import { memo, useMemo, useState } from 'react';
import { useTerminalStore, selectAllSignals } from '../../stores/terminal.store.js';
import { formatPriceWithSymbol, timeAgo } from '@arc/shared';
import type { UTBotSignal, TimeframeKey, SignalConfirmation } from '@arc/shared';
import { mergeFeedSignals, signalKey } from '../../utils/chartSignals.js';
import { BotConfigForm } from './BotConfigForm.js';

const ALL_TIMEFRAMES: TimeframeKey[] = ['1m', '5m', '15m', '30m', '1h', '4h', '1D', '1W'];
const BASE_TIMEFRAMES: TimeframeKey[] = ['1m', '5m', '15m', '1h', '4h'];

// ─── Confirmation badge ───────────────────────────────────────────────────────

const VERDICT_STYLE: Record<SignalConfirmation['verdict'], { mark: string; color: string }> = {
  CONFIRM: { mark: '✓', color: 'var(--color-buy)' },
  MIXED:   { mark: '~', color: 'var(--color-accent)' },
  REJECT:  { mark: '✗', color: 'var(--color-sell)' },
};

function ConfirmationBadge({ c }: { c: SignalConfirmation }) {
  const { mark, color } = VERDICT_STYLE[c.verdict];
  const tip = [
    `${c.verdict}: ${c.passed}/${c.total} checks`,
    ...c.checks.map(k => `${k.pass === null ? '·' : k.pass ? '✓' : '✗'} ${k.name}: ${k.detail}`),
    c.ai ? `AI (${c.ai.model}): ${c.ai.verdict}: ${c.ai.reason}` : 'AI: off (no provider configured)',
    'Shadow mode: shown for comparison, doesn\'t block auto-trade.',
  ].join('\n');
  return (
    <span className="text-[11px] font-bold cursor-help whitespace-nowrap" style={{ color }} title={tip} aria-label={tip}>
      {mark} {c.passed}/{c.total}
    </span>
  );
}

// ─── Signal Row ───────────────────────────────────────────────────────────────

const SignalRow = memo(function SignalRow({ signal }: { signal: UTBotSignal }) {
  const isBuy = signal.type === 'BUY';

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-2 py-1.5 border-b border-[#0a2040] font-mono text-[11px]">
      <span className={`
        px-1.5 py-0.5 text-[11px] font-bold tracking-wider
        ${isBuy
          ? 'bg-[rgba(0,255,136,0.12)] border border-[rgba(0,255,136,0.4)] text-buy'
          : 'bg-[rgba(255,59,59,0.12)]  border border-[rgba(255,59,59,0.4)]  text-sell'
        }
      `}>
        {signal.type}
      </span>

      <span className="text-bright min-w-[42px] tracking-wide">{signal.symbol}</span>

      {signal.confirmation && <ConfirmationBadge c={signal.confirmation} />}

      {signal.provisional && (
        <span className="badge badge-signal cursor-help" title="Candle still forming: this signal can still disappear, and the bot only trades signals on closed candles">
          FORMING
        </span>
      )}

      <span className="text-muted flex-1 text-[11px]">
        {formatPriceWithSymbol(signal.price)}
      </span>

      <span className="text-[#888] text-[11px] min-w-[22px] text-center">
        {signal.timeframe}
      </span>

      <span
        className="text-[#888] text-[11px] cursor-help"
        title={`Settings used: KEY VAL ${signal.config.keyValue} · ATR PERIOD ${signal.config.atrPeriod}`}
      >
        K{signal.config.keyValue}·P{signal.config.atrPeriod}
      </span>

      <span className="text-accent text-[11px] cursor-help" title="ATR value: Average True Range (volatility) at the signal candle — not the ATR PERIOD setting">
        ATR:{signal.atr > 1 ? signal.atr.toFixed(1) : signal.atr.toFixed(4)}
      </span>

      <span className="text-muted opacity-90 text-[11px]">
        {timeAgo(signal.timestamp)}
      </span>
    </div>
  );
});

// ─── Signal Feed ──────────────────────────────────────────────────────────────

export const SignalFeed = memo(function SignalFeed() {
  const serverSignals = useTerminalStore(selectAllSignals);
  const panels        = useTerminalStore(s => s.activePanels);
  const candles       = useTerminalStore(s => s.candles);
  const bot           = useTerminalStore(s => s.settings.defaultConfig);
  const [tab, setTab] = useState<'signals' | 'config'>('signals');
  const [selectedTf, setSelectedTf] = useState<TimeframeKey | 'ALL'>('ALL');

  // The bot's signals; charted symbol + timeframes are recomputed with the
  // bot settings so the list changes as soon as those settings do.
  const allSignals = useMemo(
    () => mergeFeedSignals(serverSignals, panels, candles, bot),
    [serverSignals, panels, candles, bot],
  );
  // Standard filters plus any other timeframe a chart shows (e.g. 30m, 1D).
  const timeframes = ALL_TIMEFRAMES.filter(tf => BASE_TIMEFRAMES.includes(tf) || allSignals.some(s => s.timeframe === tf));

  const signals = selectedTf === 'ALL'
    ? allSignals
    : allSignals.filter(s => s.timeframe === selectedTf);

  return (
    <div className="panel h-full flex flex-col">
      {/* Tabs: the signal list, and the selected chart's UT Bot settings */}
      <div className="flex border-b border-[#0a2040]" role="tablist">
        {([
          ['signals', `SIGNALS · ${signals.length}`],
          ['config',  `BOT CONFIG · K${bot.keyValue}·P${bot.atrPeriod}`],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`flex-1 min-w-0 truncate px-2 py-1.5 font-mono text-[11px] tracking-wider transition-colors border-b-2 ${
              tab === id ? 'text-arc border-arc bg-[rgba(0,212,255,0.08)]' : 'text-bright border-transparent hover:text-arc'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'config' ? <BotConfigForm /> : (<>
      {/* Timeframe filter */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-[#0a2040]">
        {(['ALL', ...timeframes] as const).map(tf => (
          <button
            key={tf}
            onClick={() => setSelectedTf(tf)}
            aria-pressed={selectedTf === tf}
            className={`
              px-2 py-0.5 text-[11px] font-mono tracking-wider transition-colors
              ${selectedTf === tf
                ? 'bg-[rgba(0,212,255,0.15)] border border-[rgba(0,212,255,0.5)] text-arc'
                : 'border border-[#1a3a5c] text-bright hover:border-arc'
              }
            `}
          >
            {tf}
          </button>
        ))}
      </div>

      {signals.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-muted font-mono text-[11px] tracking-widest animate-pulse">
              {selectedTf === 'ALL' ? 'AWAITING SIGNALS...' : `NO ${selectedTf} SIGNALS`}
            </div>
            <div className="text-muted font-mono text-[11px] mt-2 opacity-90">
              UT BOT SCANNING MARKET
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {signals.map(sig => (
            <SignalRow key={signalKey(sig)} signal={sig} />
          ))}
        </div>
      )}
      </>)}
    </div>
  );
});
